import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Card,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Iconify from '../../components/iconify';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../layouts/loading';
import { WfhRequestForm, WfhTable, WfhFilters } from './components';
import {
  createWfhRequest,
  getMyWfhRequests,
  getAllWfhRequests,
  cancelWfhRequest,
  updateWfhStatus,
  updateWfhRequest,
  exportWfhRequests,
} from '../../services/wfhService';
import { isAdmin, isManager, isHR, checkComponentPermission } from '../../utils/share';

const queryClient = new QueryClient();

function WfhContent() {
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingWfh, setEditingWfh] = useState(null);
  const isAdminUser = isAdmin();
  const isManagerUser = isManager();
  const isHRUser = isHR();
  const [activeTab, setActiveTab] = useState(() => ((isManagerUser || isHRUser) && !isAdminUser ? 1 : 0));
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingWfhId, setCancelingWfhId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const isAdminOrManagerOrHR = isAdminUser || isManagerUser || isHRUser;

  const queryClientInstance = useQueryClient();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === undefined || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      setPagination((prev) => ({ ...prev, page: 1 }));
      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ page: 1, pageSize: newPageSize });
  };

  const { data: myWfhData = { rows: [], count: 0 }, isLoading: isLoadingMyWfh } = useQuery({
    queryKey: ['myWfhRequests', filters, pagination],
    queryFn: async () => {
      try {
        const response = await getMyWfhRequests(filters, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching your WFH requests');
        return { rows: [], count: 0 };
      }
    },
  });

  const myWfhRequests = myWfhData.rows || [];
  const myWfhCount = myWfhData.count || 0;

  const { data: allWfhData = { rows: [], count: 0 }, isLoading: isLoadingAllWfh } = useQuery({
    queryKey: ['allWfhRequests', filters, pagination],
    queryFn: async () => {
      try {
        const response = await getAllWfhRequests(filters, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching all WFH requests');
        return { rows: [], count: 0 };
      }
    },
    enabled: isAdminOrManagerOrHR,
  });

  const allWfhRequests = allWfhData.rows || [];
  const allWfhCount = allWfhData.count || 0;

  const invalidateWfhQueries = () => {
    queryClientInstance.invalidateQueries(['myWfhRequests']);
    if (isAdminOrManagerOrHR) {
      queryClientInstance.invalidateQueries(['allWfhRequests']);
    }
  };

  const createWfhMutation = useMutation({
    mutationFn: createWfhRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'WFH request submitted successfully');
      setOpenFormModal(false);
      setEditingWfh(null);
      invalidateWfhQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error submitting WFH request');
    },
  });

  const updateWfhMutation = useMutation({
    mutationFn: ({ id, data }) => updateWfhRequest(id, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'WFH request updated successfully');
      setOpenFormModal(false);
      setEditingWfh(null);
      invalidateWfhQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating WFH request');
    },
  });

  const cancelWfhMutation = useMutation({
    mutationFn: cancelWfhRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'WFH request deleted successfully');
      invalidateWfhQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error deleting WFH request');
    },
  });

  const handleCreateClick = () => {
    setEditingWfh(null);
    setOpenFormModal(true);
  };

  const handleEditWfh = useCallback(
    (wfhId) => {
      const wfhToEdit =
        activeTab === 0 ? myWfhRequests.find((w) => w.id === wfhId) : allWfhRequests.find((w) => w.id === wfhId);
      if (wfhToEdit) {
        setEditingWfh(wfhToEdit);
        setOpenFormModal(true);
      }
    },
    [activeTab, myWfhRequests, allWfhRequests]
  );

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
    setEditingWfh(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (values) => {
      if (editingWfh) {
        await updateWfhMutation.mutateAsync({ id: editingWfh.id, data: values });
      } else {
        await createWfhMutation.mutateAsync(values);
      }
      return true;
    },
    [createWfhMutation, updateWfhMutation, editingWfh]
  );

  const handleCancelWfh = useCallback((wfhId) => {
    setCancelingWfhId(wfhId);
    setCancelDialogOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (cancelingWfhId) {
      cancelWfhMutation.mutate(cancelingWfhId);
      setCancelDialogOpen(false);
      setCancelingWfhId(null);
    }
  }, [cancelingWfhId, cancelWfhMutation]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExportClick = async () => {
    try {
      setIsExporting(true);
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      await exportWfhRequests(currentMonth, currentYear, filters);
      toast.success('WFH requests exported successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error exporting WFH requests');
    } finally {
      setIsExporting(false);
    }
  };

  const effectiveTab = activeTab;
  const isLoading = effectiveTab === 0 ? isLoadingMyWfh : isLoadingAllWfh;
  const currentWfhRequests = effectiveTab === 0 ? myWfhRequests : allWfhRequests;
  const currentWfhCount = effectiveTab === 0 ? myWfhCount : allWfhCount;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'WFH', path: '' },
  ];

  return (
    <>
      <Helmet>
        <title> WFH </title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            {effectiveTab === 1 && isAdminOrManagerOrHR && checkComponentPermission('READ_WFH') && (
              <LoadingButton
                variant="outlined"
                startIcon={<Iconify icon="eva:download-fill" />}
                onClick={handleExportClick}
                loading={isExporting}
              >
                Export Excel
              </LoadingButton>
            )}
            {effectiveTab === 0 && checkComponentPermission('CREATE_WFH') && (
              <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleCreateClick}>
                Request WFH
              </Button>
            )}
          </Stack>
        </Stack>

        {isAdminOrManagerOrHR ? (
          <Card sx={{ mb: 3 }}>
            <Tabs value={effectiveTab} onChange={handleTabChange}>
              <Tab label="My WFH" />
              <Tab label="All WFH" />
            </Tabs>
          </Card>
        ) : (
          <Card sx={{ mb: 3 }}>
            <Tabs value={0} sx={{ pointerEvents: 'none' }}>
              <Tab label="My WFH" />
            </Tabs>
          </Card>
        )}

        <WfhFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          showEmployeeFilter={effectiveTab === 1 && isAdminOrManagerOrHR}
        />

        <Loading open={isLoading} />
        <Card>
          <Box sx={{ p: 3 }}>
            <WfhTable
              wfhRequests={currentWfhRequests}
              onCancelWfh={handleCancelWfh}
              onEditWfh={handleEditWfh}
              showEmployee={effectiveTab === 1}
              currentUserId={userId}
              count={currentWfhCount}
              page={pagination.page - 1}
              rowsPerPage={pagination.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        {openFormModal && (
          <WfhRequestForm
            open
            onClose={handleFormModalClose}
            onSubmit={handleFormSubmit}
            wfhData={editingWfh}
            isEdit={!!editingWfh}
          />
        )}

        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Delete WFH Request</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this WFH request? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleConfirmCancel}
              loading={cancelWfhMutation.isLoading}
            >
              Yes, Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}

export default function WfhPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <WfhContent />
    </QueryClientProvider>
  );
}
